/**
 * OSM and MapTiler + Geoapify configuration and utilities
 */

// Geoapify (primary for route tiles and static maps)
export function getGeoapifyKey(): string {
    return (
        (import.meta as any).env?.VITE_GEOAPIFY_KEY ||
        (process as any)?.env?.VITE_GEOAPIFY_KEY ||
        (window as any).VINTMAP_CONFIG?.geoapifyKey ||
        ""
    );
}

/**
 * Geoapify raster tile URL for Leaflet (route maps only).
 * Style "osm-bright" closely matches the look of standard OSM.
 */
export function getGeoapifyTileUrl(style = "osm-bright"): string {
    const key = getGeoapifyKey();
    if (!key) return "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"; // fallback
    return `https://maps.geoapify.com/v1/tile/${style}/{z}/{x}/{y}.png?apiKey=${key}`;
}

export function getGeoapifyStaticMapUrl(options: {
    markers?: Array<{ lat: number | string; lng: number | string; label?: string; color?: string }>;
    polyline?: string;
    width?: number;
    height?: number;
    zoom?: number;
}): string {
    const key = getGeoapifyKey();
    if (!key) return "";

    const { markers = [], width = 600, height = 600 } = options;
    if (markers.length === 0 && !options.polyline) return "";

    // Sanitize and convert to numbers (Drizzle/API might return strings)
    const valid = markers.map(m => ({
        lat: Number(String(m.lat).replace(",", ".")),
        lng: Number(String(m.lng).replace(",", ".")),
        label: m.label
    })).filter(m => !isNaN(m.lat) && !isNaN(m.lng));

    // Build URL using native URLSearchParams for reliable encoding
    const url = new URL("https://maps.geoapify.com/v1/staticmap");
    url.searchParams.set("style", "osm-bright");
    url.searchParams.set("width", width.toString());
    url.searchParams.set("height", height.toString());
    url.searchParams.set("scaleFactor", "2");

    // Auto-fit if zoom/center NOT provided
    if (options.zoom !== undefined) {
        url.searchParams.set("zoom", options.zoom.toString());

        // Manual center calculation if zoom is forced
        const lats = valid.map(m => m.lat);
        const lngs = valid.map(m => m.lng);
        const minLat = Math.min(...lats), maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
        const centerLat = (minLat + maxLat) / 2;
        const centerLng = (minLng + maxLng) / 2;
        url.searchParams.set("center", `lonlat:${centerLng},${centerLat}`);
    }

    url.searchParams.set("apiKey", key);

    // Use multiple &marker params, letting URLSearchParams handle encoding
    valid.forEach(m => {
        let markerStr = `lonlat:${m.lng},${m.lat};color:#f90f5a;size:medium`;
        if (m.label) {
            markerStr += `;text:${m.label};textcolor:#f90f5a`;
        }
        url.searchParams.append("marker", markerStr);
    });

    // Add polyline if provided
    if (options.polyline) {
        url.searchParams.set("geometry", `polyline5:${options.polyline};linecolor:#f90f5a;linewidth:4`);
    }

    return url.toString();
}

// ─── Legacy MapTiler (kept for backwards compat, used by shop listing maps) ───

export function getMapTilerKey(): string {
    return (
        (import.meta as any).env?.VITE_MAPTILER_KEY ||
        (process as any)?.env?.NEXT_PUBLIC_MAPTILER_KEY ||
        (window as any).VINTMAP_CONFIG?.mapTilerKey ||
        ""
    );
}

export const MAPTILER_KEY = getMapTilerKey(); // Legacy support

export const getMapTilerTileUrl = (style = "streets-v2") => {
    return `https://api.maptiler.com/maps/${style}/{z}/{x}/{y}.png?key=${getMapTilerKey()}`;
};

/**
 * Generate MapTiler Static Map URL (used by shop listing map, NOT routes)
 */
export function getMapTilerStaticMapUrl(options: {
    center?: { lat: number, lng: number },
    zoom?: number,
    width?: number,
    height?: number,
    markers?: Array<{ lat: number, lng: number, color?: string }>,
    path?: string,
    style?: string
}): string {
    const key = getMapTilerKey();
    if (!key) return "";

    const style = options.style || "basic-v2";
    const width = options.width || 800;
    const height = options.height || 600;
    const zoom = options.zoom || 12;

    const params = new URLSearchParams({ key });

    let baseUrl: string;
    if (options.center) {
        baseUrl = `https://api.maptiler.com/maps/${style}/static/${options.center.lng},${options.center.lat},${zoom}/${width}x${height}.png`;
    } else {
        // auto-fit requires premium - skip, caller should use Geoapify instead
        return "";
    }

    if (options.markers && options.markers.length > 0) {
        const markersStr = options.markers.map(m => `${m.lng},${m.lat}`).join('|');
        params.append('markers', `color:0xF90F5A|${markersStr}`);
    }

    if (options.path) {
        params.append('path', `stroke:0xF90F5A|width:2|enc:${options.path}`);
    }

    return `${baseUrl}?${params.toString()}`;
}

/**
 * Generate OSM Static Map URL (Free Fallback for shop listings — NOT routes)
 * Routes use getGeoapifyStaticMapUrl() instead.
 */
export function getOsmStaticMapUrl(options: {
    markers?: Array<{ lat: number, lng: number, label?: string }>,
    width?: number,
    height?: number
}): string {
    // For routes, use getGeoapifyStaticMapUrl() - this is only for shops
    return getGeoapifyStaticMapUrl(options);
}

export const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | <a href="https://www.geoapify.com/">Geoapify</a>';

export const PINK_COLOR = "#F90F5A";

/**
 * Polyline decoder helper
 */
export function decodePolyline(encoded: string): [number, number][] {
    let index = 0, lat = 0, lng = 0;
    const coordinates: [number, number][] = [];
    const len = encoded.length;

    while (index < len) {
        let b, shift = 0, result = 0;
        do {
            b = encoded.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lat += dlat;

        shift = 0;
        result = 0;
        do {
            b = encoded.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (b >= 0x20);
        const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lng += dlng;

        coordinates.push([lat / 1e5, lng / 1e5]);
    }

    return coordinates;
}
